import React from "react";
import { ColumnsType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import { TFunction } from "i18next";
import { nanoid } from "nanoid";

import Warning from "~/components/Icons/alert-square";
import Violation from "~/components/Icons/icon-violations";
import { IconMimeType } from "~/libs/resolveMemeType";
import { labelsForDocumentStatus } from "~/pages/Documents/utils/utils";
import { InfoTooltip } from "~/shared/ui/Tooltip";

import s from "~/components/Table/TableRow/DefaultTableRow/TableRow.less";

/** Колонки таблицы документов, по которым возможна клиентская сортировка */
export const DOCUMENT_SORTABLE_COLUMNS = [
  "name",
  "signedBy",
  "status",
  "createdBy",
  "createdAt",
  "importance",
];

const localeCompare = (a: unknown, b: unknown) =>
  String(a ?? "").localeCompare(String(b ?? ""));

const COMPARATORS: Record<string, (a: any, b: any) => number> = {
  name: (a, b) => localeCompare(a.name, b.name),
  signedBy: (a, b) => localeCompare(a.signedBy, b.signedBy),
  status: (a, b) =>
    localeCompare(labelsForDocumentStatus[a.status], labelsForDocumentStatus[b.status]),
  createdBy: (a, b) => localeCompare(a.createdBy, b.createdBy),
  createdAt: (a, b) => (a.createdAtRaw ?? 0) - (b.createdAtRaw ?? 0),
  importance: (a, b) =>
    Number(a.importance === "urgent") - Number(b.importance === "urgent"),
};

export const getDocumentsColumns = (
  t: TFunction,
  options: string[],
  sortField?: string,
  sortType?: string
): ColumnsType<any> =>
  options.map((option) => {
    const comparator = COMPARATORS[option];
    const sortOrder: SortOrder | undefined =
      comparator &&
      sortField === option &&
      (sortType === "ASC" || sortType === "DESC")
        ? sortType === "DESC"
          ? "descend"
          : "ascend"
        : undefined;

    const baseColumn = {
      title: t(`documents.table.${option}`),
      dataIndex: option,
      key: option,
      ...(comparator ? { sorter: comparator, sortOrder } : {}),
    };

    // Special render for Files field - show file links in grid
    if (option === "Files") {
      return {
        ...baseColumn,
        render: (value: any) => {
          if (!value || !Array.isArray(value)) return null;
          return (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "2px",
              }}
            >
              {value.map((file) => (
                <span key={nanoid()} className={s.cont}>
                  {!!file?.link && (
                    <a
                      href={file.link}
                      download
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      placeholder={file.name}
                      rel="noreferrer"
                    >
                      {IconMimeType(file)}
                    </a>
                  )}
                </span>
              ))}
            </div>
          );
        },
      };
    }

    if (option === "description") {
      return {
        ...baseColumn,
        render: (value: unknown, record: { description?: string | null }) => {
          const raw = value ?? record?.description;
          const content = raw != null && raw !== "" ? String(raw) : "";
          if (!content) return null;
          return (
            <div
              style={{ display: "flex", alignItems: "center" }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <InfoTooltip content={content} />
            </div>
          );
        },
      };
    }

    // Special render for importance field - show icons
    if (option === "importance") {
      return {
        ...baseColumn,
        render: (value: any) =>
          value === "urgent" ? (
            <Violation
              fill={"#db0505"}
              color={"#fff"}
              width={"22"}
              height={"22"}
            />
          ) : (
            <span className={s.svgImg}>
              <Warning />
            </span>
          ),
      };
    }

    // Special render for status field - translate status
    if (option === "status") {
      return {
        ...baseColumn,
        render: (value: any) => <span>{labelsForDocumentStatus[value]}</span>,
      };
    }

    return {
      ...baseColumn,
      render: (value: any) => <span>{value}</span>,
    };
  });
